import CMSPlaceholder from '@/features/shared/CMSPlaceholder';
import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import { usePortalBoxHandler, withGTM } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import { ColorNames, SpacingSizes, Theme } from '@/utils/constants';
import { getPageUrl } from '@/utils/helpers';
import { resolveBool, resolveSpacingSize, resolveTheme } from '@/utils/resolvers';
import React, { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Breadcrumb from './Breadcrumb';
import { BREADCRUMB_BOX_PORTAL_EVENT, BREADCRUMB_BOX_PORTAL_ID } from './Breadcrumb.constants';
import { onDatalayer } from './Breadcrumb.datalayer';

const BreadcrumbGTM = withGTM({ Component: Breadcrumb, onDatalayer });

const MappedBreadcrumb = ({ presentation, breadcrumbData, isPreview }: any) => {
  const { backgroundColor, datalayerVariant } = presentation;
  const { items: breadCrumpItems, separator } = breadcrumbData || {};
  const { elementPortalBox: breadcrumbBox } = usePortalBoxHandler({
    boxId: BREADCRUMB_BOX_PORTAL_ID,
    readyEventName: BREADCRUMB_BOX_PORTAL_EVENT,
  });

  const breadCrumbProps = useMemo(
    () => ({
      separator,
      theme: resolveTheme('bg-color')({ rawVal: backgroundColor }),
      items: breadCrumpItems,
    }),
    [backgroundColor, breadCrumpItems, separator],
  );

  if (breadCrumbProps.items?.length === 0) {
    return (
      <div>
        {isPreview ? (
          <CMSPlaceholder componentName="Breadcrumb" errorMessage="Doesn't work on root page" />
        ) : (
          <span
            data-datalayer-position="breadcrumb"
            data-datalayer-variant={datalayerVariant}
            aria-hidden="true"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: '<!-- Breadcrumb render -->',
            }}
          />
        )}
      </div>
    );
  }

  if (isPreview) {
    return <CMSPlaceholder componentName="Breadcrumb" message="" />;
  }

  return (
    <div>
      {breadcrumbBox ? (
        createPortal(<BreadcrumbGTM {...breadCrumbProps} />, breadcrumbBox, 'breadcrumb')
      ) : (
        <ContentContainer
          topSpacing={SpacingSizes.small}
          bottomSpacing={SpacingSizes.small}
          topBackgroundColor={ColorNames.charcoal}
          bottomBackgroundColor={ColorNames.charcoal}
        >
          <MuiContainer>
            <BreadcrumbGTM {...breadCrumbProps} theme={Theme.dark} />
          </MuiContainer>
        </ContentContainer>
      )}
    </div>
  );
};

const resolveBreadcrumbData = ({ rawVal, page }: any) => {
  return {
    items: rawVal.items.map(({ title, link }: any) => ({
      title,
      link: {
        href: getPageUrl({ url: link, page }),
      },
    })),
  };
};

const NewsCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    breadcrumbData: [null, resolveBreadcrumbData],
    componentParameterMap: [
      'presentation',
      {
        bottomSpacing: [null, resolveSpacingSize],
        displayInBanner: [null, resolveBool],
        'variant-label': 'datalayerVariant',
      },
    ],
  });

  return <CMSWrapper brProps={brProps} render={MappedBreadcrumb} mapping={mapping.current} />;
};

export default NewsCMSWrapper;
